import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Portal } from "@chakra-ui/react";

/**
 * Renders children inside a Chakra UI Portal and positions a Box relative
 * to the given target element. The Box width matches the target's width.
 *
 * @param {{
 *   targetRef: React.RefObject<HTMLElement>,
 *   placement?: 'bottom' | 'top' | 'left' | 'right',
 *   offset?: number,
 *   isOpen?: boolean,
 *   width?: number | string,
 *   containerProps: import("@chakra-ui/react").BoxProps
 *   children?: React.ReactNode,
 * }} props
 * @returns {JSX.Element | null}
 */
const CustomPortal = ({
  targetRef,
  placement = "bottom",
  offset = 0,
  isOpen = true,
  width,
  children,
  containerProps,
}) => {
  const boxRef = useRef(null);
  const [targetWidth, setTargetWidth] = useState(0);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const getScrollOffsets = () => ({
    x: window.pageXOffset || document.documentElement.scrollLeft || 0,
    y: window.pageYOffset || document.documentElement.scrollTop || 0,
  });

  const updateBoxSize = () => {
    const el = boxRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setBoxSize({ width: rect.width, height: rect.height });
  };

  const parseWidthToPx = (value) => {
    if (value == null) return null;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      if (value.endsWith("px")) {
        const n = parseFloat(value.replace("px", ""));
        return Number.isFinite(n) ? n : null;
      }
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const updatePosition = () => {
    const target = targetRef?.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const { x: scrollX, y: scrollY } = getScrollOffsets();

    const width = rect.width;
    const height = boxSize.height;
    const providedWidthPx = parseWidthToPx(width);
    const effectiveBoxWidth = providedWidthPx ?? boxSize.width;

    let top = rect.bottom + scrollY;
    let left = rect.left + scrollX;

    switch (placement) {
      case "top":
        top = rect.top + scrollY - height - offset;
        // center horizontally over the target when width differs
        left = rect.left + scrollX + (rect.width - effectiveBoxWidth) / 2;
        break;
      case "left":
        top = rect.top + scrollY;
        left = rect.left + scrollX - effectiveBoxWidth - offset;
        break;
      case "right":
        top = rect.top + scrollY;
        left = rect.right + scrollX + offset;
        break;
      case "bottom":
      default:
        top = rect.bottom + scrollY + offset;
        // center horizontally over the target when width differs
        left = rect.left + scrollX + (rect.width - effectiveBoxWidth) / 2;
        break;
    }

    setTargetWidth(width);
    setCoords({ top, left });
  };

  // Measure target width and initial position on mount and when refs/placement change
  useLayoutEffect(() => {
    updateBoxSize();
    updatePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, placement, offset, isOpen, width]);

  // Re-measure the box when its size may change (children updates)
  useEffect(() => {
    updateBoxSize();
    // After measuring, position again to account for new size (e.g., top/left)
    const id = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  // Listen to window scroll/resize
  useEffect(() => {
    const onScroll = () => updatePosition();
    const onResize = () => {
      updateBoxSize();
      updatePosition();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    placement,
    offset,
    targetRef,
    width,
    isOpen,
    boxSize.height,
    boxSize.width,
  ]);

  // Observe target size changes
  useEffect(() => {
    const target = targetRef?.current;
    if (!target || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      updateBoxSize();
      updatePosition();
    });
    ro.observe(target);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRef, placement, offset, isOpen, width]);

  // Observe portal box size changes (post/children changes)
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      updateBoxSize();
      updatePosition();
    });
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placement, offset, isOpen, width]);

  // If not open or no target, render nothing
  if (!isOpen || !targetRef?.current) return null;

  const widthStyle =
    width != null
      ? typeof width === "number"
        ? `${width}px`
        : width
      : `${targetWidth}px`;

  return (
    <Portal>
      <Box
        ref={boxRef}
        position="absolute"
        top={coords.top}
        left={coords.left}
        width={widthStyle}
        // Avoid layout jump before first measurement
        visibility={width != null || targetWidth ? "visible" : "hidden"}
        zIndex={1000}
        {...containerProps}
      >
        {children}
      </Box>
    </Portal>
  );
};

export default CustomPortal;
